import React, { useState } from 'react';
import { CalendarClock, Clock, ArrowRight, ArrowLeft, CheckCircle2, ChevronDown, Sparkles } from 'lucide-react';
import { parseLocalDateTime, formatTime, formatDateMedium } from '../../utils/timeUtils';
import type { RoutySlotOptionsArtifact, RoutySlotOption } from '../../business/routy/routyArtifacts';

/** Slots shown before the "More times" expander (mirrors the chatbot's top-3). */
const INITIAL_SLOT_COUNT = 3;

interface SlotOptionsCardProps {
  artifact: RoutySlotOptionsArtifact;
  disabled?: boolean;
  onSelect: (artifact: RoutySlotOptionsArtifact, option: RoutySlotOption) => void;
}

function categoryTone(category: string): string {
  const c = category.toLowerCase();
  if (c.includes('top')) return 'bg-blue-50 text-blue-700 border-blue-200';
  if (c.includes('proximity') || c.includes('closest')) return 'bg-info-50 text-info-700 border-info-200';
  if (c.includes('customer')) return 'bg-violet-50 text-violet-700 border-violet-200';
  return 'bg-ink-100 text-ink-600 border-ink-200';
}

const SlotOptionsCard: React.FC<SlotOptionsCardProps> = ({ artifact, disabled, onSelect }) => {
  const [pickedIndex, setPickedIndex] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const locked = disabled || pickedIndex !== null;

  const heading =
    artifact.kind === 'reschedule'
      ? `Move ${artifact.appointmentLabel || 'your appointment'}`
      : artifact.bookingSummary || 'Choose a time';

  if (artifact.options.length === 0) return null;

  const visibleOptions = showAll ? artifact.options : artifact.options.slice(0, INITIAL_SLOT_COUNT);
  const hiddenCount = artifact.options.length - visibleOptions.length;

  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-ink-500" />
        <span className="text-sm font-semibold text-ink-900 truncate">{heading}</span>
        {artifact.durationMinutes != null && (
          <span className="ml-auto text-2xs font-medium text-ink-500 bg-surface border border-ink-200 px-2 py-0.5 rounded-md">
            {artifact.durationMinutes} min
          </span>
        )}
      </div>

      <div className="p-3 space-y-2.5">
        {visibleOptions.map((opt, index) => {
          const start = parseLocalDateTime(opt.date, opt.startTime);
          const end = parseLocalDateTime(opt.date, opt.endTime);
          const isPicked = pickedIndex === index;
          const addedDrive =
            opt.driveInMinutes != null || opt.driveOutMinutes != null
              ? (opt.driveInMinutes ?? 0) + (opt.driveOutMinutes ?? 0)
              : null;

          return (
            <div
              key={`${opt.slotId ?? ''}-${opt.date}-${opt.startTime}-${index}`}
              className={`rounded-lg border p-3 transition-colors ${
                isPicked ? 'border-emerald-300 ring-1 ring-emerald-200 bg-emerald-50/40' : 'border-ink-200 bg-surface'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span
                  className={`text-2xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md border ${categoryTone(
                    opt.category,
                  )}`}
                >
                  {opt.category}
                </span>
                {opt.routeScore != null && (
                  <span className="ml-auto text-2xs text-ink-400">score {Math.round(opt.routeScore)}</span>
                )}
              </div>

              <div className="flex items-start gap-2">
                <CalendarClock className="w-4 h-4 mt-0.5 flex-shrink-0 text-ink-400" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink-900">
                    {start ? formatDateMedium(start) : opt.date}
                  </p>
                  <p className="text-xs text-ink-600 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {start ? formatTime(start) : opt.startTime}
                    {end ? ` – ${formatTime(end)}` : ''}
                  </p>
                </div>
              </div>

              {(opt.driveInMinutes != null || opt.driveOutMinutes != null) && (
                <div className="mt-2 rounded-md bg-blue-50/70 border border-blue-100 px-2.5 py-1.5 space-y-1">
                  {opt.driveInMinutes != null && (
                    <div className="flex items-start gap-1.5 text-2xs text-ink-700">
                      <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0 text-blue-500" />
                      <span>
                        <span className="font-semibold">{opt.driveInMinutes} min</span> from{' '}
                        {opt.previousLabel || 'your start'}
                      </span>
                    </div>
                  )}
                  {opt.driveOutMinutes != null && (
                    <div className="flex items-start gap-1.5 text-2xs text-ink-700">
                      <ArrowLeft className="w-3 h-3 mt-0.5 flex-shrink-0 text-indigo-500" />
                      <span>
                        <span className="font-semibold">{opt.driveOutMinutes} min</span> to{' '}
                        {opt.nextLabel || 'your next stop'}
                      </span>
                    </div>
                  )}
                  {addedDrive != null && (
                    <p className="text-2xs text-ink-400">+{addedDrive} min of driving added</p>
                  )}
                </div>
              )}

              <button
                onClick={() => {
                  setPickedIndex(index);
                  onSelect(artifact, opt);
                }}
                disabled={locked}
                className={`mt-2.5 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all active:scale-[0.99] disabled:cursor-not-allowed ${
                  isPicked
                    ? 'bg-emerald-600 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40'
                }`}
              >
                {isPicked ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Selected
                  </>
                ) : (
                  <>
                    {artifact.kind === 'reschedule' ? 'Move here' : 'Book this time'}
                  </>
                )}
              </button>
            </div>
          );
        })}

        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            disabled={locked}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-ink-300 text-xs font-medium text-ink-600 hover:bg-ink-50 hover:text-ink-800 transition-colors disabled:opacity-50"
          >
            <ChevronDown className="w-3.5 h-3.5" />
            Show {hiddenCount} more time{hiddenCount > 1 ? 's' : ''}
          </button>
        )}
      </div>
    </div>
  );
};

export default SlotOptionsCard;
